class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.mainProduct = this.closest('main-product');
    this.trigger = this.mainProduct?.querySelector('.product-buy-buttons');

    if (!this.mainProduct || !this.trigger) return;

    this.isVisible = false;
    this.hideTimer = null;
    this.resizeTicking = false;

    this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
    this.minusButton = this.querySelector('[data-sticky-quantity-minus]');
    this.plusButton = this.querySelector('[data-sticky-quantity-plus]');
    this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option-select]'));
    this.addButton = this.querySelector('[data-sticky-add-to-cart]');

    this.syncQuantityFromReal();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.mainProduct.addEventListener('change', this.handleMainProductChange.bind(this), { signal });
    this.mainProduct.addEventListener('input', this.handleMainProductChange.bind(this), { signal });

    this.quantityInput?.addEventListener('input', () => this.syncQuantityToReal(), { signal });
    this.quantityInput?.addEventListener('change', () => this.syncQuantityToReal(), { signal });

    this.minusButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.stepQuantity(-1);
    }, { signal });

    this.plusButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.stepQuantity(1);
    }, { signal });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => this.syncOptionToReal(select), { signal });
    });

    this.addButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.mainProduct.querySelector('.product-buy-buttons [data-add-to-cart]')?.click();
    }, { signal });

    this.setupVisibilityObserver();

    window.addEventListener('resize', this.handleResize.bind(this), { signal, passive: true });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.visibilityObserver?.disconnect();
    window.clearTimeout(this.hideTimer);
    window.clearTimeout(this.limitMessageTimer);
    document.documentElement.style.setProperty('--sticky-cart-height', '0px');
  }

  setupVisibilityObserver() {
    if (!('IntersectionObserver' in window)) {
      this.hidden = false;
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleVisibility(scrolledPast);
    });

    this.visibilityObserver.observe(this.trigger);
  }

  handleResize() {
    if (this.resizeTicking || !this.isVisible) return;
    this.resizeTicking = true;

    requestAnimationFrame(() => {
      this.updateStickyCartHeightVar();
      this.resizeTicking = false;
    });
  }

  updateStickyCartHeightVar() {
    document.documentElement.style.setProperty('--sticky-cart-height', `${this.offsetHeight}px`);
  }

  toggleVisibility(show) {
    if (show === this.isVisible) return;
    this.isVisible = show;

    window.clearTimeout(this.hideTimer);

    if (show) {
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.updateStickyCartHeightVar();
      requestAnimationFrame(() => this.classList.add('is-visible'));
    } else {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      this.hideTimer = window.setTimeout(() => {
        if (!this.isVisible) {
          this.hidden = true;
          document.documentElement.style.setProperty('--sticky-cart-height', '0px');
        }
      }, 300);
    }
  }

  getRealQuantityInput() {
    return this.mainProduct.querySelector('.product-buy-buttons [data-quantity-input]');
  }

  handleMainProductChange(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('sticky-add-to-cart')) return;
    if (!event.target.matches('[data-quantity-input]')) return;

    this.syncQuantityFromReal();
  }

  syncQuantityFromReal() {
    const realInput = this.getRealQuantityInput();
    if (!realInput || !this.quantityInput) return;

    if (this.quantityInput.value !== realInput.value) {
      this.quantityInput.value = realInput.value;
    }

    this.syncStepperButtons();
    this.hideLimitMessage();
  }

  syncQuantityToReal() {
    const realInput = this.getRealQuantityInput();
    if (!realInput || !this.quantityInput) return;
    if (realInput.value === this.quantityInput.value) return;

    realInput.value = this.quantityInput.value;
    realInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncQuantityFromReal();
  }

  stepQuantity(direction) {
    if (!this.quantityInput) return;

    const step = Number(this.quantityInput.step) || 1;
    const min = Number(this.quantityInput.min) || 1;
    const max = this.quantityInput.max !== '' ? Number(this.quantityInput.max) : Number.POSITIVE_INFINITY;
    const current = Number(this.quantityInput.value) || min;

    if (direction > 0 && current >= max) {
      // Already at the limit - the click had no effect, which reads as a
      // bug unless we say why.
      this.showLimitMessage(max);
      return;
    }

    const next = Math.min(Math.max(current + step * direction, min), max);

    this.quantityInput.value = String(next);
    this.syncQuantityToReal();
    this.syncStepperButtons();

    if (direction > 0 && next >= max) {
      this.showLimitMessage(max);
    } else {
      this.hideLimitMessage();
    }
  }

  syncStepperButtons() {
    if (!this.quantityInput) return;

    const min = Number(this.quantityInput.min) || 1;
    const max = this.quantityInput.max !== '' ? Number(this.quantityInput.max) : Number.POSITIVE_INFINITY;
    const current = Number(this.quantityInput.value) || min;

    // aria-disabled only (not the native disabled attribute): a truly
    // disabled button never dispatches click events, which would make
    // stepQuantity's limit-message branch unreachable on the next click.
    if (this.minusButton) {
      this.minusButton.setAttribute('aria-disabled', String(current <= min));
    }

    if (this.plusButton) {
      this.plusButton.setAttribute(
        'aria-disabled',
        String(Number.isFinite(max) && current >= max),
      );
    }
  }

  showLimitMessage(max) {
    const message = this.querySelector('[data-sticky-quantity-message]');
    if (!message) return;

    const template = message.dataset.quantityMessageTemplate;

    message.textContent = template
      ? template.replace('[quantity]', String(max))
      : String(max);

    message.hidden = false;

    window.clearTimeout(this.limitMessageTimer);
    this.limitMessageTimer = window.setTimeout(() => {
      message.hidden = true;
    }, 3000);
  }

  hideLimitMessage() {
    const message = this.querySelector('[data-sticky-quantity-message]');
    if (message) {
      message.hidden = true;
    }

    window.clearTimeout(this.limitMessageTimer);
  }

  syncOptionToReal(select) {
    const optionValueId = select.value;
    const realInput = this.mainProduct.querySelector(
      `.product-options [data-option-value-input][data-option-value-id="${this.escapeSelector(optionValueId)}"]`,
    );

    if (!realInput) return;

    if (realInput.tagName === 'OPTION') {
      const realSelect = realInput.parentElement;
      if (!realSelect) return;
      realSelect.value = optionValueId;
      realSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    realInput.checked = true;
    realInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  escapeSelector(value) {
    if (window.CSS?.escape) return CSS.escape(String(value ?? ''));
    return String(value ?? '').replace(/["\\]/g, '\\$&');
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
